import * as APITypes from "../../../../api/cur8/generated-types/v1/Api";
import { makePath } from "../../../../api/url";
import { applyParams } from "../../../params";
import { applyPagination } from "../../pagination";
import { getJSON, postJSON, putJSON } from "../../request";
import { APIContext } from "../../types";

export type ConsentAPI = ReturnType<typeof createConsentAPI>;

export function createConsentAPI({
  buildURL,
  fetchPagedJSON,
  fetchJSON,
  fetchRaw,
}: APIContext) {
  function getListOfConsents({
    patientId,
    ...rest
  }: APITypes.PatientConsentQueryParams) {
    const path = makePath(["api", "v1", "patient", patientId, "consent"]);
    const url = buildURL(path);
    applyParams(url.searchParams, {
      ...rest,
    });
    applyPagination(url.searchParams, { ...rest });
    const request = getJSON(url);

    return fetchPagedJSON<APITypes.ConsentSummary>(request);
  }
  function saveNewConsent(
    patientId: string,
    body: APITypes.SignConsentRequest
  ) {
    const path = makePath(["api", "v1", "patient", patientId, "consent"]);
    const url = buildURL(path);

    const request = postJSON(url, body);

    return fetchJSON<APITypes.Consent>(request);
  }

  function revokeConsent({
    patientId,
    consentId,
  }: {
    patientId: string;
    consentId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "consent",
      consentId,
      "revoke",
    ]);
    const url = buildURL(path);
    const request = putJSON(url, undefined);

    return fetchJSON<APITypes.Consent>(request);
  }

  function exclude({
    patientId,
    consentId,
    body,
  }: {
    patientId: string;
    consentId: string;
    body: APITypes.SetExclusionCriteriaRequest;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "consent",
      consentId,
      "exclude",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, body);

    return fetchJSON<APITypes.Consent>(request);
  }

  function getConsent({
    patientId,
    consentId,
  }: {
    patientId: string;
    consentId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "consent",
      consentId,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);

    return fetchJSON<APITypes.Consent>(request);
  }

  function counterSignConsent({
    patientId,
    consentId,
  }: {
    patientId: string;
    consentId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "consent",
      consentId,
      "countersign",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, undefined);

    return fetchJSON<APITypes.Consent>(request);
  }

  function optOutFromConsentFor({
    patientId,
    body,
  }: {
    patientId: string;
    body: APITypes.SignConsentRequest;
  }) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "consent",
      "opt-out",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, body);

    return fetchJSON<APITypes.Consent>(request);
  }

  function getRenderConsentDocumentURL({
    patientId,
    documentUri,
    token,
  }: APITypes.PatientConsentRenderParams) {
    const path = makePath([
      "api",
      "v1",
      "patient",
      patientId,
      "consent",
      "render",
    ]);

    const url = buildURL(path);
    applyParams(url.searchParams, {
      patientId,
      documentUri,
      token,
    });

    return url;
  }

  return {
    optOutFromConsentFor,
    revokeConsent,
    saveNewConsent,
    getListOfConsents,
    getConsent,
    counterSignConsent,
    exclude,
    getRenderConsentDocumentURL,
  };
}
